import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import Logo from "@/components/Logo";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Loader2, ShieldCheck, Phone } from "lucide-react";

export default function ForgotPassword() {
  const navigate = useNavigate();
  const { formatApiError } = useAuth();
  const [stage, setStage] = useState("mobile");  // mobile | otp | success
  const [mobile, setMobile] = useState("");
  const [otp, setOtp] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [loading, setLoading] = useState(false);
  const [resendIn, setResendIn] = useState(0);

  useEffect(() => {
    if (resendIn <= 0) return;
    const t = setTimeout(() => setResendIn((v) => v - 1), 1000);
    return () => clearTimeout(t);
  }, [resendIn]);

  const requestOtp = async (e) => {
    e?.preventDefault?.();
    if (!mobile || mobile.length < 10) { toast.error("Enter your 10-digit mobile"); return; }
    setLoading(true);
    try {
      await api.post("/auth/otp/send", { mobile, purpose: "password_reset" });
      toast.success(`OTP sent to ${mobile}`);
      setStage("otp");
      setResendIn(45);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Failed to send OTP");
    } finally { setLoading(false); }
  };

  const confirmReset = async (e) => {
    e?.preventDefault?.();
    if (otp.length !== 6) { toast.error("Enter the 6-digit OTP"); return; }
    if ((newPwd || "").length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setLoading(true);
    try {
      await api.post(`/auth/forgot-password/confirm?mobile=${encodeURIComponent(mobile)}&code=${encodeURIComponent(otp)}&new_password=${encodeURIComponent(newPwd)}`);
      toast.success("Password reset successful — please login with your new password.");
      setStage("success");
      setTimeout(() => navigate("/login", { replace: true }), 1500);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Reset failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <Link to="/" className="flex justify-center mb-6"><Logo size={52} /></Link>
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8">
          {stage === "mobile" && (
            <>
              <div className="flex items-center gap-2">
                <Phone size={20} className="text-amber-500" />
                <h1 className="heading text-2xl font-bold">Forgot Password</h1>
              </div>
              <p className="text-sm text-slate-500 mt-1">We&apos;ll send a one-time password to your registered mobile.</p>
              <form onSubmit={requestOtp} className="mt-6 space-y-4">
                <div>
                  <label className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Registered Mobile</label>
                  <input type="tel" required value={mobile} onChange={(e) => setMobile(e.target.value.replace(/\D/g, ""))}
                    placeholder="10-digit mobile"
                    className="mt-1 w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-brand outline-none"
                    data-testid="forgot-mobile-input" />
                </div>
                <button type="submit" disabled={loading}
                  className="w-full bg-brand hover:bg-[#172b66] disabled:opacity-60 text-white font-semibold rounded-lg py-3 flex items-center justify-center gap-2"
                  data-testid="forgot-send-otp">
                  {loading && <Loader2 size={16} className="animate-spin" />} Send OTP
                </button>
              </form>
            </>
          )}

          {stage === "otp" && (
            <>
              <div className="flex items-center gap-2">
                <ShieldCheck size={20} className="text-emerald-500" />
                <h1 className="heading text-2xl font-bold">Set New Password</h1>
              </div>
              <p className="text-sm text-slate-500 mt-1">Enter the OTP sent to <b>{mobile}</b> and your new password.</p>
              <form onSubmit={confirmReset} className="mt-6 space-y-4">
                <div>
                  <label className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">OTP</label>
                  <input type="text" inputMode="numeric" maxLength={6} required value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                    className="mt-1 w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-amber-500 outline-none text-center text-lg font-mono tracking-[0.4em]"
                    data-testid="forgot-otp-input" />
                </div>
                <div>
                  <label className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">New Password</label>
                  <input type="password" required minLength={6} value={newPwd}
                    onChange={(e) => setNewPwd(e.target.value)}
                    placeholder="At least 6 characters"
                    className="mt-1 w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-brand outline-none"
                    data-testid="forgot-new-password-input" />
                </div>
                <button type="submit" disabled={loading}
                  className="w-full bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white font-semibold rounded-lg py-3 flex items-center justify-center gap-2"
                  data-testid="forgot-reset-btn">
                  {loading && <Loader2 size={16} className="animate-spin" />} Reset Password
                </button>
              </form>
              <div className="mt-4 flex items-center justify-between text-xs text-slate-500">
                <button type="button" onClick={() => setStage("mobile")} className="hover:text-brand font-semibold">← Change number</button>
                {resendIn > 0 ? (
                  <span>Resend in {resendIn}s</span>
                ) : (
                  <button type="button" onClick={requestOtp} className="text-brand font-semibold hover:underline" data-testid="forgot-resend-otp">Resend OTP</button>
                )}
              </div>
            </>
          )}

          {stage === "success" && (
            <div className="text-center py-6">
              <div className="text-5xl">✅</div>
              <div className="mt-3 heading text-lg font-bold">Password reset</div>
              <div className="text-sm text-slate-500 mt-1">Redirecting to login...</div>
            </div>
          )}
        </div>
        <div className="mt-5 text-center text-xs text-slate-500">
          <Link to="/login" className="hover:text-brand">← Back to login</Link>
        </div>
      </div>
    </div>
  );
}
